import { ApiServiceApplicationDocumentKind } from "@rmp/core/types/services/serviceDocumentKind";
import { ServiceWorkflowTransitionCode } from "@rmp/core/types/services/workflow/serviceWorkflow";
import { ServiceApplicationClosingDocumentsMode } from "@rmp/core/types/services/serviceApplicationClosingDocumentsMode";

export type ServiceDocumentGroupCode = "act" | "invoice" | "factureInvoice";

export type ServiceDocumentGroupMeta = {
	label: string;
	kind: ApiServiceApplicationDocumentKind;
};

export type ServiceDocumentTransitionMeta = {
	requiredGroups?: readonly ServiceDocumentGroupCode[];
	optionalPreUploadGroups?: readonly ServiceDocumentGroupCode[];
	signGroups?: readonly ServiceDocumentGroupCode[];
};

export const SERVICE_DOCUMENT_GROUPS = {
	act: {
		label: "Акт об оказании услуг",
		kind: ApiServiceApplicationDocumentKind.ServiceAcceptanceCertificate
	},
	invoice: {
		label: "Счет",
		kind: ApiServiceApplicationDocumentKind.Invoice
	},
	factureInvoice: {
		label: "Счет-фактура",
		kind: ApiServiceApplicationDocumentKind.FactureInvoice
	}
} as const satisfies Record<ServiceDocumentGroupCode, ServiceDocumentGroupMeta>;

export const SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS = {
	AttachAct: {
		requiredGroups: ["act"],
		optionalPreUploadGroups: ["invoice", "factureInvoice"]
	},
	SignActByEnterprise: {
		signGroups: ["act"]
	},
	AttachAccountingDocuments: {
		requiredGroups: ["invoice", "factureInvoice"]
	},
	SignAccountingDocumentsByEnterprise: {
		signGroups: ["invoice", "factureInvoice"]
	},
	SignActByCounterparty: {
		signGroups: ["act"]
	}
} as const satisfies Partial<Record<ServiceWorkflowTransitionCode, ServiceDocumentTransitionMeta>>;

export const ENTERPRISE_DOCUMENT_WORKFLOW_TRANSITIONS = new Set<ServiceWorkflowTransitionCode>([
	"AttachAct",
	"SignActByEnterprise",
	"AttachAccountingDocuments",
	"SignAccountingDocumentsByEnterprise"
]);

export const COUNTERPARTY_DOCUMENT_WORKFLOW_TRANSITIONS = new Set<ServiceWorkflowTransitionCode>([
	"SignActByCounterparty"
]);

const SERVICE_DOCUMENT_WORKFLOW_TRANSITION_MAP: Partial<Record<ServiceWorkflowTransitionCode, ServiceDocumentTransitionMeta>> =
	SERVICE_DOCUMENT_WORKFLOW_TRANSITIONS;

function getTransitionMeta(code: string): ServiceDocumentTransitionMeta | undefined {
	return SERVICE_DOCUMENT_WORKFLOW_TRANSITION_MAP[code as ServiceWorkflowTransitionCode];
}

function getKinds(groups: readonly ServiceDocumentGroupCode[] | undefined): ApiServiceApplicationDocumentKind[] {
	return Array.from(new Set((groups ?? []).map(group => SERVICE_DOCUMENT_GROUPS[group].kind)));
}

export function getRequiredDocumentKindsForTransition(code: string, mode?: ServiceApplicationClosingDocumentsMode | null): ApiServiceApplicationDocumentKind[] {
	if(mode === ServiceApplicationClosingDocumentsMode.Utd)
		return code === "AttachAccountingDocuments" ? [ApiServiceApplicationDocumentKind.Utd] : [];
	return getKinds(getTransitionMeta(code)?.requiredGroups);
}

export function getOptionalPreUploadDocumentKindsForTransition(code: string, mode?: ServiceApplicationClosingDocumentsMode | null): ApiServiceApplicationDocumentKind[] {
	if(mode === ServiceApplicationClosingDocumentsMode.Utd)
		return code === "AttachAccountingDocuments" ? [ApiServiceApplicationDocumentKind.Invoice] : [];
	return getKinds(getTransitionMeta(code)?.optionalPreUploadGroups);
}

export function getRequiredSignableDocumentKindsForTransition(code: string, mode?: ServiceApplicationClosingDocumentsMode | null): ApiServiceApplicationDocumentKind[] {
	if(mode === ServiceApplicationClosingDocumentsMode.Utd)
		return code === "SignAccountingDocumentsByEnterprise" ? [ApiServiceApplicationDocumentKind.Utd] : [];
	return getKinds(getTransitionMeta(code)?.signGroups);
}

export function getSignableDocumentKindsForTransition(code: string, mode?: ServiceApplicationClosingDocumentsMode | null): ApiServiceApplicationDocumentKind[] {
	const requiredKinds = getRequiredSignableDocumentKindsForTransition(code, mode);
	if(mode === ServiceApplicationClosingDocumentsMode.Utd && code === "SignAccountingDocumentsByEnterprise")
		return [...requiredKinds, ApiServiceApplicationDocumentKind.Invoice];
	return requiredKinds;
}

export function isEnterpriseDocumentTransition(code: string): boolean {
	return ENTERPRISE_DOCUMENT_WORKFLOW_TRANSITIONS.has(code as ServiceWorkflowTransitionCode);
}

export function isCounterpartyDocumentTransition(code: string): boolean {
	return COUNTERPARTY_DOCUMENT_WORKFLOW_TRANSITIONS.has(code as ServiceWorkflowTransitionCode);
}
